/* ============================================================================
   bymee-tracking.js — outil partagé, chargé UNE fois, utilisé par TOUTES
   les mini-apps (quiz, Loox maison, Videowise maison, etc.).

   Ne contient AUCUNE logique propre à une feature — juste 2 fonctions
   génériques (track / captureCustomer) + 1 aide à la mesure de durée
   (trackTimeSpent). Chaque mini-app importe ce fichier et appelle ces
   fonctions à ses propres moments, sans jamais réécrire la logique réseau.

   Deux destinations, jamais mélangées (cf. worker-bymee/03_ARCHITECTURE_
   DECISIONS.md) :
   - track()          -> POST /collect           -> GA4 (stats anonymes)
   - captureCustomer() -> POST /customer/capture  -> Shopify (fiche cliente)

   Destination : assets/bymee-tracking.js, chargé tôt (pas besoin d'être
   différé comme gtag.js — ce fichier ne fait rien tant qu'une mini-app
   ne l'appelle pas explicitement, coût au repos ~0).
   ============================================================================ */

(function (window) {
  'use strict';

  var WORKER_URL = 'https://worker-bymee.bymee-cosmetics.workers.dev';

  /* --------------------------------------------------------------------
     client_id — réutilise UNIQUEMENT celui de gtag.js (cookie _ga).
     Volontairement AUCUN repli (décision du 11/10/2026) : si gtag.js n'a
     pas encore posé son cookie (pas encore chargé, consentement pas
     encore donné), on préfère ne RIEN envoyer plutôt que d'envoyer sous
     un identifiant temporaire non lié -- un vrai bug (ex. gtag.js cassé)
     se verrait alors directement comme "zéro event reçu", pas comme des
     events discrets et mal reliés, beaucoup plus difficiles à repérer.
     -------------------------------------------------------------------- */
  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function getClientId() {
    // Cookie _ga posé par gtag.js : format "GA1.1.<id1>.<id2>"
    var ga = getCookie('_ga');
    if (ga) {
      var parts = ga.split('.');
      if (parts.length >= 4) return parts[2] + '.' + parts[3];
    }
    return null; // pas de repli, volontairement
  }

  /* --------------------------------------------------------------------
     Consentement analytics — même règle que le tag gtag différé (cf.
     theme.liquid), centralisée ici pour que CHAQUE appel à track() soit
     automatiquement protégé, sans que chaque mini-app ait à y penser.
     -------------------------------------------------------------------- */
  function hasAnalyticsConsent() {
    try {
      return window.Shopify && window.Shopify.customerPrivacy &&
        window.Shopify.customerPrivacy.currentVisitorConsent().analytics === 'yes';
    } catch (e) {
      return false;
    }
  }

  /* --------------------------------------------------------------------
     track(nomEvent, params) — envoie un event anonyme à GA4 via /collect.
     JAMAIS d'email, de nom, ou de donnée personnelle dans params.
     Silencieusement ignoré si le consentement analytics n'est pas donné.
     -------------------------------------------------------------------- */
  function track(nomEvent, params) {
    if (!hasAnalyticsConsent()) return;
    var clientId = getClientId();
    if (!clientId) return; // gtag.js pas encore prêt -- on n'envoie rien, pas de repli
    try {
      fetch(WORKER_URL + '/collect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: nomEvent,
          params: params || {},
          client_id: clientId
        })
      }).catch(function () {});
    } catch (e) {}
  }

  /* --------------------------------------------------------------------
     Consentement marketing déjà donné — flag LOCAL au navigateur
     (localStorage), PAS une vérification serveur. Décision du
     14/10/2026 : éviter de redemander la même case sur plusieurs
     mini-apps (quiz, popup newsletter, futures apps) sans créer de
     route publique "cet email est-il déjà abonné ?" -- une telle route
     permettrait à n'importe qui de tester des emails pour savoir qui
     est cliente, fuite de donnée refusée explicitement par le
     propriétaire. Limite acceptée : ne couvre que CE navigateur/
     appareil, redemande sur un autre device -- compromis volontaire.
     -------------------------------------------------------------------- */
  function hasMarketingConsent() {
    try {
      return window.localStorage.getItem('bymee_marketing_consent') === 'yes';
    } catch (e) {
      return false;
    }
  }

  function rememberMarketingConsent(value) {
    try {
      if (value) window.localStorage.setItem('bymee_marketing_consent', 'yes');
    } catch (e) {}
  }

  /* --------------------------------------------------------------------
     captureCustomer(email, tags, acceptsMarketing, source, website) —
     écrit une fiche cliente Shopify via /customer/capture. Contient un
     email, jamais envoyé à GA4, jamais à track().

     website = valeur du champ honeypot caché côté thème. Une vraie
     visiteuse le laisse toujours vide ; un bot qui remplit tout le
     remplit aussi. Le Worker rejette silencieusement si non-vide (cf.
     src/index.js, handleCustomerCapture). Sans ce paramètre, le
     honeypot ajouté côté thème ne servirait à rien -- jamais transmis.
     -------------------------------------------------------------------- */
  /* locale (optionnel, 6e param., ajouté le 16/10) : langue de la page ('fr'/'en').
     Le Worker la pose sur la fiche client ; Flow s'en sert pour choisir le modèle
     d'email. Absente = comportement d'avant (langue par défaut de la boutique). */
  function captureCustomer(email, tags, acceptsMarketing, source, website, locale) {
    rememberMarketingConsent(acceptsMarketing === true); // écrit le flag ici -- aucun appelant n'a à y penser
    return fetch(WORKER_URL + '/customer/capture', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email,
        tags: tags || [],
        accepts_marketing: acceptsMarketing === true,
        source: source || 'inconnu',
        website: website || '',
        locale: locale || ''
      })
    });
  }

  /* --------------------------------------------------------------------
     trackTimeSpent(nomEvent, params) — chrono générique. Retourne une
     fonction arreter() à appeler quand l'action se termine (question
     suivante, avis suivant, etc.). Même mécanique pour toutes les
     mini-apps, jamais dupliquée.
     -------------------------------------------------------------------- */
  function trackTimeSpent(nomEvent, params) {
    var debut = Date.now();
    return function arreter(extra) {
      var seconds = Math.round((Date.now() - debut) / 1000);
      track(nomEvent, Object.assign({}, params, extra, { seconds: seconds }));
    };
  }

  window.BymeeTrack = {
    track: track,
    captureCustomer: captureCustomer,
    trackTimeSpent: trackTimeSpent,
    getClientId: getClientId,
    hasMarketingConsent: hasMarketingConsent
  };
})(window);
